/**
 * Subscription Gate Component
 * Shows subscription modal when user doesn't have an active subscription
 * Blocks access to the app until subscription is active
 */

import React, { useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../hooks/useAuth';
import { Crown, Check, Sparkles, LogOut } from 'lucide-react';
import './SubscriptionGate.css';

function SubscriptionGate({ children }) {
  const { isPremium, plans, loading: subLoading, startCheckout, formatPrice } = useSubscription();
  const { logout, user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);

  // Show loading while checking subscription
  if (subLoading) {
    return (
      <div className="subscription-gate-loading">
        <div className="loading-content">
          <Sparkles className="loading-icon" size={48} />
          <h2>Loading...</h2>
          <p>Checking subscription status</p>
        </div>
      </div>
    );
  }

  // If user has active subscription, show the app
  if (isPremium) {
    return children;
  }

  // Handle subscription checkout
  const handleSubscribe = async (planId) => {
    setError(null);
    setSelectedPlan(planId);
    setCheckoutLoading(true);

    try {
      await startCheckout(planId);
    } catch (err) {
      setError(err.message || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
      setSelectedPlan(null);
    }
  };

  // Sort plans: Pro first, then Elite
  const sortedPlans = [...plans].sort((a, b) => a.price_cents - b.price_cents);

  return (
    <div className="subscription-gate">
      <div className="subscription-gate-backdrop"></div>

      <div className="subscription-gate-modal">
        <div className="subscription-gate-header">
          <div className="gate-logo">
            <img src="/skate.png" alt="Lumi" className="gate-logo-icon" />
            <span className="gate-logo-text">Lumi<span className="gate-logo-skating">Skating</span></span>
          </div>
          <h1>Choose Your Plan</h1>
          <p>Welcome, {user?.name || 'Skater'}! Select a subscription plan to start your training journey.</p>
        </div>

        {error && (
          <div className="subscription-gate-error">
            {error}
          </div>
        )}

        <div className="subscription-gate-plans">
          {sortedPlans.map((plan) => (
            <div
              key={plan.id}
              className={`gate-plan-card ${plan.id === 'elite' ? 'featured' : ''}`}
            >
              {plan.id === 'elite' && (
                <div className="gate-plan-badge">
                  <Crown size={14} />
                  Best Value
                </div>
              )}

              <h3 className="gate-plan-name">{plan.name}</h3>

              <div className="gate-plan-price">
                <span className="price-amount">{formatPrice(plan.price_cents)}</span>
                <span className="price-period">/month</span>
              </div>

              <p className="gate-plan-description">{plan.description}</p>

              <ul className="gate-plan-features">
                <li>
                  <Check size={16} className="feature-check" />
                  {plan.video_upload_limit ? `${plan.video_upload_limit} video uploads/month` : 'Unlimited video uploads'}
                </li>
                <li>
                  <Check size={16} className="feature-check" />
                  AI coaching feedback
                </li>
                <li>
                  <Check size={16} className="feature-check" />
                  Training planning
                </li>
                <li>
                  <Check size={16} className="feature-check" />
                  Video comparison
                </li>
                <li>
                  <Check size={16} className="feature-check" />
                  Coach notes
                </li>
                {plan.id === 'elite' && (
                  <li>
                    <Check size={16} className="feature-check" />
                    Priority support
                  </li>
                )}
              </ul>

              <button
                className={`gate-plan-button ${plan.id === 'elite' ? 'primary' : 'secondary'}`}
                onClick={() => handleSubscribe(plan.id)}
                disabled={checkoutLoading}
              >
                {checkoutLoading && selectedPlan === plan.id ? (
                  'Redirecting to checkout...'
                ) : (
                  <>Subscribe to {plan.name}</>
                )}
              </button>
            </div>
          ))}
        </div>

        <div className="subscription-gate-footer">
          <p className="gate-refund-policy">
            7-day full refund if no videos have been uploaded
          </p>
          <button className="gate-logout-button" onClick={logout}>
            <LogOut size={16} />
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionGate;
